import React, { RefObject, useEffect, useRef, useState } from "react";
import { Location } from "react-router-dom";
import { useAlert } from "./AlertContextComponent";
import Logger from "../utils/Logger";

interface PreventSnipToolScreenShotProps {
  location: Location;
}
const targetPaths = [
  "/dashboard/recommendation",
  "/dashboard/learning-room",
  "/dashboard/student",
];

//Prevent  Keys(Print,Inspect,Save,Paste)
const PreventScreenshotAndPrint: React.FC = () => {
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const disableKeys = (e: KeyboardEvent) => {
      if (
        e.ctrlKey &&
        (e.key === "I" ||
          e.key === "J" ||
          e.key === "U" ||
          e.key === "S" ||
          e.key === "P")
      ) {
        e.preventDefault();
      }
    };

    const handleBeforePrint = () => {
      document.body.style.filter = "blur(10px)";
      alert("Printing is restricted!");
    };

    const handleAfterPrint = () => {
      document.body.style.filter = "none";
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        document.body.style.filter = "blur(100px)";
      } else {
        document.body.style.filter = "none";
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", disableKeys);
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", disableKeys);
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return null;
};
//Overlay Styles -Balck Screen
const Overlay: React.FC<{ show: boolean }> = ({ show }) => {
  if (!show) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9998,
        background: "black",
        color: "rgba(255, 0, 0, 0.3)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <p
        style={{
          fontFamily: "Mazzard",
          fontSize: 24,
          fontWeight: 400,
          textAlign: "center",
        }}
      >
        Confidential Content - Jozuna
      </p>
    </div>
  );
};

//Prevent PrtScr Key
const PreventPrtScrKey: React.FC<PreventSnipToolScreenShotProps> = ({
  location,
}) => {
  const [showWatermark, setShowWatermark] = useState(false);
  const handleKeyDown = (e: KeyboardEvent) => {
    const showWatermarkTemporarily = () => {
      setShowWatermark(true);
      setTimeout(() => setShowWatermark(false), 3000);
    };

    const isPrintScreen = e.key === "PrintScreen" || e.keyCode === 44;
    const isMetaKey = e.key === "Meta" && e.code === "";

    const isWindowsShortcut = e.shiftKey && e.metaKey; //Windows+Shift+S
    const isScreenRecordShortcut = e.metaKey && e.altKey; // Windows + Alt + R
    // macOS screen capture: Shift + Cmd + (3, 4, 5)
    const isMacScreenshot =
      e.metaKey &&
      e.shiftKey &&
      ["Digit3", "Digit4", "Digit5"].includes(e.code);
    //macOS dev tools  capture:Crtl+Shift +P
    const isCmdShiftP =
      (e.metaKey || e.ctrlKey) && e.shiftKey && e.code === "KeyP";
    if (isCmdShiftP) {
      e.preventDefault();
    }

    if (isMacScreenshot) {
      // e.preventDefault();
      showWatermarkTemporarily();
    }
    if (isPrintScreen || isMetaKey) {
      e.preventDefault();
      showWatermarkTemporarily();
    }

    if (isWindowsShortcut) {
      e.preventDefault();
      showWatermarkTemporarily();
    }
    if (isScreenRecordShortcut) {
      document.body.style.filter = "blur(10px)";
    }
  };
  const handleFocusChange = () => {
    document.body.style.filter = "none";
  };
  useEffect(() => {
    const isTargetPath = targetPaths.some((path) =>
      location.pathname.startsWith(path),
    );
    if (!isTargetPath) return;
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focus", handleFocusChange);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focus", handleFocusChange);
    };
  }, [location.pathname]);

  return <Overlay show={showWatermark} />;
};

const PreventSnipTool: React.FC<PreventSnipToolScreenShotProps> = ({
  location,
}) => {
  const [isBlurred, setIsBlurred] = useState(false);
  const { isAlertVisible } = useAlert();

  const isActive = targetPaths.some((path) =>
    location.pathname.startsWith(path),
  );

  const handleVisibilityChange = () => {
    if (isActive) {
      setIsBlurred(false);
    }
  };

  const handleWindowBlur = () => {
    if (isActive) setIsBlurred(true);
  };

  const handleWindowFocus = () => {
    if (isActive) {
      setIsBlurred(false);
    }
  };
  const handleClick = () => {
    if (isActive && isBlurred) {
      setIsBlurred(true);
    }
  };
  useEffect(() => {
    if (!isActive) return;

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("click", handleClick);

    window.addEventListener("focus", handleWindowFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [isActive]);

  const shouldShowOverlay = isActive && !isAlertVisible && isBlurred;

  return shouldShowOverlay ? <Overlay show={true} /> : null;
};

const ClipBoardImageDetection: React.FC<PreventSnipToolScreenShotProps> = ({
  location,
}) => {
  const [showAlert, setShowAlert] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isReading = useRef(false);

  const readClipboardWithTimeout = (): Promise<ClipboardItems> => {
    return Promise.race([
      navigator.clipboard.read(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Timeout reading clipboard")), 3000),
      ),
    ]);
  };

  const readClipboard = async () => {
    if (!window.isSecureContext) {
      Logger.warn("Clipboard access requires HTTPS or localhost.");
      return;
    }

    if (!document.hasFocus()) {
      Logger.info("⛔ Document not focused — skipping clipboard read");
      return;
    }

    try {
      const permission = await navigator.permissions.query({
        name: "clipboard-read" as PermissionName,
      });

      if (permission.state !== "granted" && permission.state !== "prompt") {
        Logger.warn("🚫 Clipboard read not permitted");
        return;
      }

      const clipboardItems = await readClipboardWithTimeout();
      for (const item of clipboardItems) {
        if (
          item.types.includes("image/png") ||
          item.types.includes("image/jpeg")
        ) {
          const blob = await item.getType(item.types[0]);
          Logger.info("📸 Image detected in clipboard", blob);
          await navigator.clipboard.writeText(""); // Clear clipboard
          setShowAlert(true);
          break;
        }
      }
    } catch (err: any) {
      if (err.name === "NotAllowedError") {
        Logger.warn(
          "📋 Clipboard read not allowed — user gesture may be required.",
        );
      } else if (err.message?.includes("Timeout")) {
        Logger.warn("⌛ Clipboard read timeout");
      } else {
        Logger.error("❌ Clipboard read failed", err);
      }
    }
  };

  const safeReadClipboard = async () => {
    if (isReading.current) return;
    isReading.current = true;
    await readClipboard();
    isReading.current = false;
  };

  const startPolling = () => {
    if (intervalRef.current) return;
    Logger.info("✅ Starting clipboard polling...");
    intervalRef.current = setInterval(() => {
      safeReadClipboard();
    }, 2000);
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      Logger.info("🛑 Stopped clipboard polling.");
    }
  };

  useEffect(() => {
    if (targetPaths.some((path) => location.pathname.startsWith(path))) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [location.pathname]);

  return (
    <>
      {showAlert && (
        <div
          style={{
            position: "fixed",
            top: "20%",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#f44336",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
            zIndex: 1000,
          }}
        >
          ⚠️ Screenshot or image detected on clipboard!
          <button
            style={{ marginLeft: "20px", padding: "5px 10px" }}
            onClick={() => setShowAlert(false)}
          >
            Close
          </button>
        </div>
      )}
    </>
  );
};

/**
 * This component injects the NoPrint.js script to disable printing,
 * copying, screenshots, and enables auto blur on route change.
 */
const SCRIPT_ID = "noprint-js-script";
const NoPrintProtection: React.FC<PreventSnipToolScreenShotProps> = ({
  location,
}) => {
  const removeScript = () => {
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      (window as any).noPrint = false;
      (window as any).noCopy = false;
      (window as any).noScreenshot = false;
      (window as any).autoBlur = false;
      // existingScript.remove();
      if (location.pathname === "/dashboard/assessment") {
        window.location.reload();
      }
    }
  };
  useEffect(() => {
    const shouldEnable = targetPaths.some((path) =>
      location.pathname.startsWith(path),
    );

    if (shouldEnable) {
      (window as any).noPrint = true;
      (window as any).noCopy = true;
      (window as any).noScreenshot = true;
      (window as any).autoBlur = true;

      const alreadyExists = document.getElementById(SCRIPT_ID);
      if (!alreadyExists) {
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = "https://pdfanticopy.com/noprint.js";
        script.type = "text/javascript";
        script.async = true;

        document.body.appendChild(script);
      }
    } else {
      removeScript();
    }

    return () => {
      removeScript();
    };
  }, [location.pathname]);

  return null;
};

export {
  PreventScreenshotAndPrint,
  PreventPrtScrKey,
  ClipBoardImageDetection,
  PreventSnipTool,
  NoPrintProtection,
};
